import { CONTEXT_PROFILE_FILES, type ContextProfileKind } from '@fos/shared/contracts/context';
import type { MemoryUpdateResult } from '@fos/shared/contracts/copilot';
import { Check, Sparkles } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Textarea } from '../../../ui/Input';
import { Select } from '../../../ui/Select';
import { copilotErrorInfo, metaLine } from '../copilot-ui';

type Target = 'auto' | ContextProfileKind;

/**
 * „Zapamiętaj …” (MVP §11, Etap 6): AI zapisuje polecenie jako jedną zasadę i wybiera profil (OWNER.md — styl i zasady,
 * BUSINESS.md — oferta i procesy). Powstaje propozycja zmiany z diffem i źródłem, która czeka na akceptację
 * właściciela; profil zmienia się dopiero po niej.
 */
export function RememberDialog({
  open,
  onOpenChange,
  initialText = '',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialText?: string;
}) {
  const [text, setText] = useState(initialText);
  const [target, setTarget] = useState<Target>('auto');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [result, setResult] = useState<MemoryUpdateResult | null>(null);
  const utils = trpc.useUtils();
  const run = trpc.copilot.proposeMemoryUpdate.useMutation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset przy każdym otwarciu
  useEffect(() => {
    if (!open) return;
    setText(initialText);
    setTarget('auto');
    setResult(null);
    setFieldError(null);
    run.reset();
  }, [open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (text.trim().length < 3) {
      setFieldError('Napisz, co AI ma zapamiętać.');
      return;
    }
    setFieldError(null);
    try {
      const res = await run.mutateAsync({ text: text.trim(), ...(target === 'auto' ? {} : { profileKind: target }) });
      setResult(res);
      void utils.context.invalidate();
    } catch {
      // błąd pod formularzem
    }
  };

  const error = run.isError ? copilotErrorInfo(run.error) : null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Zapamiętaj"
      description="AI zaproponuje zmianę pamięci. Zapisze się dopiero po akceptacji właściciela."
      footer={
        result ? (
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Zamknij
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              icon={Sparkles}
              type="submit"
              form="copilot-remember-form"
              loading={run.isPending}
            >
              Zaproponuj zmianę
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div role="status" className="flex flex-col gap-3">
          <p className="flex items-start gap-2 text-body-s text-fg">
            <Check size={16} strokeWidth={2} aria-hidden className="mt-0.5 shrink-0 text-success" />
            <span>
              Propozycja zmiany {CONTEXT_PROFILE_FILES[result.profileKind]} czeka na akceptację: „{result.rule}”
            </span>
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-body-s">
            {result.approvalId && (
              <Link to={`/akceptacje/${result.approvalId}`} className="text-accent hover:underline">
                Otwórz w Akceptacjach
              </Link>
            )}
            <Link to="/ustawienia/ai" className="text-accent hover:underline">
              Zobacz propozycje pamięci
            </Link>
          </div>
          <p className="text-caption text-fg-muted">{metaLine('Zasada od AI', result.meta)}</p>
        </div>
      ) : (
        <form id="copilot-remember-form" onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          <Field label="Co zapamiętać?" error={fieldError}>
            <Textarea
              autoFocus
              rows={3}
              maxLength={2_000}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setFieldError(null);
              }}
              placeholder="np. „zapamiętaj, że w mailach do klientów nie używam wykrzykników”"
            />
          </Field>
          <Field label="Gdzie" hint="Automatycznie: AI wybierze profil, Ty zobaczysz go przed akceptacją.">
            <Select<Target>
              value={target}
              onValueChange={setTarget}
              options={[
                { value: 'auto', label: 'Automatycznie' },
                { value: 'owner', label: 'OWNER.md — mój styl i zasady' },
                { value: 'business', label: 'BUSINESS.md — oferta i procesy' },
              ]}
            />
          </Field>
          {error && <FormMessage>{error.message}</FormMessage>}
        </form>
      )}
    </Dialog>
  );
}
